import React, {
  FC,
  Reducer,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import AuctionBase from "./AuctionBase";
import {
  Auction,
  AuctionKindAscending,
  AuctionKindBigNumber,
  AuctionKindGDAContinuous,
  AuctionKindGDADiscrete,
  AuctionKindJapanese,
  AuctionKindSealed,
  AuctionKindSealedFloor,
  VariantsAuction,
} from "../../types";
import { createClient } from "@supabase/supabase-js";
import { OmniAction, OmniAuctionState, actions } from "./OmniAuctionState";
import { omniReducer } from "./OmniAuctionReducer";
import { AUCTIONS, reconcileRealtimeUpdateIntoAuction } from "../../utils";
import { useOnBuyWithCard } from "../../hooks/useOnBuyWithCard";
import { NormalAuction } from "./AuctionKinds/NormalAuction";
import { GDAContinousAuction } from "./AuctionKinds/GDAContinousAuction";
import { GDADiscreteAuction } from "./AuctionKinds/GDADiscreteAuction";
import { PriceConfirm } from "./PriceConfirm/PriceConfirm";

import { getProto } from "../../utils/proto/index";
import { SealedAuction } from "./AuctionKinds/SealedAuction";
import { SealedAuctionFloor } from "./AuctionKinds/SealedAuctionFloor";
import { JapaneseAuction } from "./AuctionKinds/JapaneseAuction";
import { BigNumberAuction } from "./AuctionKinds/BigNumberAuction";
import CountdownTimer from "../CountdownTimer";
import { useCountdown } from "../../hooks";
import { AuctionBaseCard } from "./AuctionBase/Card";

interface OmniAuctionProps {
  auctionGuard: Auction;
  shopId: number;
  productId: number;
  variantsAuction: VariantsAuction;
  customerId: number;
  shopLoginUrl: string;
  now: number;
}

const OmniAuction: FC<OmniAuctionProps> = ({
  auctionGuard,
  shopId,
  productId,
  variantsAuction,
  customerId,
  shopLoginUrl,
  now,
}) => {
  const initialState: OmniAuctionState = {
    statefulPrice: 0,
    auction: variantsAuction[variantsAuction["variants"][0].native_variant_id],
    variant: variantsAuction["variants"][0],
    bidAmount: 0,
    placingBid: false,
    confirmingBid: false,
    purchaseQuantity: 1,
  };
  const [state, dispatch] = useReducer<Reducer<OmniAuctionState, OmniAction>>(
    omniReducer,
    initialState
  );

  const {
    auction,
    statefulPrice,
    variant,
    bidAmount,
    placingBid,
    confirmingBid,
    purchaseQuantity,
  } = state;

  const supabase = useMemo(
    () =>
      createClient(
        "https://ighwgtdscniyuzwffnsf.supabase.co",
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlnaHdndGRzY25peXV6d2ZmbnNmIiwicm9sZSI6ImFub24iLCJpYXQiOjE2NzExMjA3MDMsImV4cCI6MTk4NjY5NjcwM30.j3ZaSHwTptB5W3lfDxYLiHOTs4Ms6ruTXo92YlxEkcU",
        {
          realtime: {
            params: {
              eventsPerSecond: 10,
            },
          },
        }
      ),
    []
  );

  const handleAuctionUpdate = useCallback(
    (realtimeUpdate) => {
      const _auction = reconcileRealtimeUpdateIntoAuction(
        auction,
        realtimeUpdate
      );
      dispatch({ type: actions.SET_AUCTION, payload: _auction });
    },
    [auction]
  );

  useEffect(() => {
    if (getProto(window.location.search)) {
      return;
    }
    const realtimeSubscription = supabase
      .channel(
        `public:${AUCTIONS[auction.kind].TABLE}:external_id=eq.${
          auction.stateExternalId
        }`
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: AUCTIONS[auction.kind].TABLE,
          filter: `external_id=eq.${auction.stateExternalId}`,
        },
        (realtimeUpdate) => handleAuctionUpdate(realtimeUpdate)
      )
      .subscribe();

    async function realtimeUnsubscribe() {
      await realtimeSubscription.unsubscribe();
    }
  }, [supabase, handleAuctionUpdate, auction, auction.stateExternalId]);

  const { onBuyWithCard } = useOnBuyWithCard(
    () => dispatch({ type: actions.SET_CONFIRMING_BID, payload: true }),
    customerId,
    () => dispatch({ type: actions.SET_CONFIRMING_BID, payload: false })
  );

  const onPlaceBid = useCallback(
    (price: number, quantity: number) => {
      async function executor() {
        dispatch({ type: actions.SET_PLACING_BID, payload: true });
        dispatch({ type: actions.SET_BID_AMOUNT, payload: Number(price) });
        dispatch({
          type: actions.SET_PURCHASE_QUANTITY,
          payload: Number(quantity),
        });

        onBuyWithCard(auction, variant, purchaseQuantity);
      }
      executor();
    },
    [dispatch, auction, variant, purchaseQuantity]
  );

  const setStatefulPrice = (price) =>
    dispatch({ type: actions.SET_STATEFUL_PRICE, payload: price });

  const setVariant = (index: number) => {
    // TODO set corresponding auction
    const variant = variantsAuction["variants"][index];
    dispatch({
      type: actions.SET_VARIANT,
      payload: variant,
    });
    dispatch({
      type: actions.SET_AUCTION,
      payload: variantsAuction[variant.native_variant_id],
    });
  };

  const setStatefulPurchaseQuantity = (quantity: number) => {
    dispatch({ type: actions.SET_PURCHASE_QUANTITY, payload: quantity });
  };

  const [auctionStartTime, auctionEndTime] = useMemo(() => {
    if (getProto(window.location.search)) {
      const preempt = 0;
      const duration = 120;
      return [now + preempt, now + preempt + duration];
    }
    return [
      Math.floor(auction.auctionStartTime.getTime() / 1000),
      Math.floor(auction.auctionEndTime.getTime() / 1000),
    ];
  }, [auction]);

  const [timeUntilStart, setTimeUntilStart] = useState(
    Math.floor(auctionStartTime - now)
  );

  const timeUntilEnd = useCountdown(auctionEndTime * 1000);

  useEffect(() => {
    if (now > auctionEndTime) return;
    let refresh = setTimeout(() => {
      if (auctionStartTime > 0) {
        setTimeUntilStart(timeUntilStart - 1);
      }
    }, 1000);
    return () => clearTimeout(refresh);
  }, [timeUntilStart]);

  const [variantIndex, setVariantIndex] = React.useState(0);

  const onVariantSelection = (number: number) => {
    setVariantIndex(number);
    setVariant(number);
  };

  const getAuctionKind = (auction: Auction) => {
    switch (auction.layout) {
      case AuctionKindAscending: {
        return (
          <NormalAuction
            auction={auction}
            shopId={shopId}
            productId={productId}
            now={now}
            endEpoch={auctionEndTime}
            onPlaceBid={onPlaceBid}
            customerId={customerId}
            shopLoginUrl={shopLoginUrl}
            statefulPurchaseQuantity={state.purchaseQuantity}
            setStatefulPurchaseQuantity={setStatefulPurchaseQuantity}
            setVariant={setVariant}
          />
        );
      }
      case AuctionKindGDAContinuous: {
        return (
          <GDAContinousAuction
            auction={auction}
            now={now}
            endEpoch={auctionEndTime}
            customerId={customerId.toString()}
            shopLoginUrl={shopLoginUrl}
            onPlaceBid={onPlaceBid}
            currentPrice={statefulPrice}
            statefulPurchaseQuantity={state.purchaseQuantity}
            setStatefulPurchaseQuantity={setStatefulPurchaseQuantity}
            setCurrentPrice={setStatefulPrice}
            setVariant={setVariant}
          />
        );
      }
      case AuctionKindGDADiscrete: {
        return (
          <GDADiscreteAuction
            auction={auction}
            now={now}
            endEpoch={auctionEndTime}
            customerId={customerId}
            shopLoginUrl={shopLoginUrl}
            onPlaceBid={onPlaceBid}
            currentPrice={statefulPrice}
            setCurrentPrice={setStatefulPrice}
            statefulPurchaseQuantity={state.purchaseQuantity}
            setStatefulPurchaseQuantity={setStatefulPurchaseQuantity}
            setVariant={setVariant}
          />
        );
      }
      case AuctionKindSealed: {
        return (
          <SealedAuction
            auction={auction}
            shopId={shopId}
            productId={productId}
            now={now}
            endEpoch={auctionEndTime}
            onPlaceBid={onPlaceBid}
            customerId={customerId}
            shopLoginUrl={shopLoginUrl}
            statefulPurchaseQuantity={state.purchaseQuantity}
            setStatefulPurchaseQuantity={setStatefulPurchaseQuantity}
            setVariant={setVariant}
            activeBid={bidAmount}
          />
        );
      }
      case AuctionKindSealedFloor: {
        return (
          <SealedAuctionFloor
            auction={auction}
            shopId={shopId}
            productId={productId}
            now={now}
            endEpoch={auctionEndTime}
            onPlaceBid={onPlaceBid}
            customerId={customerId}
            shopLoginUrl={shopLoginUrl}
            statefulPurchaseQuantity={state.purchaseQuantity}
            setStatefulPurchaseQuantity={setStatefulPurchaseQuantity}
            setVariant={setVariant}
          />
        );
      }
      case AuctionKindJapanese: {
        return (
          <JapaneseAuction
            auction={auction}
            shopId={shopId}
            productId={productId}
            now={now}
            endEpoch={auctionEndTime}
            onPlaceBid={onPlaceBid}
            customerId={customerId}
            shopLoginUrl={shopLoginUrl}
            statefulPurchaseQuantity={state.purchaseQuantity}
            setStatefulPurchaseQuantity={setStatefulPurchaseQuantity}
            setVariant={setVariant}
          />
        );
      }
      case AuctionKindBigNumber: {
        return (
          <BigNumberAuction
            auction={auction}
            shopId={shopId}
            productId={productId}
            now={now}
            endEpoch={auctionEndTime}
            onPlaceBid={onPlaceBid}
            customerId={customerId}
            shopLoginUrl={shopLoginUrl}
            statefulPurchaseQuantity={state.purchaseQuantity}
            setStatefulPurchaseQuantity={setStatefulPurchaseQuantity}
            setVariant={setVariant}
          />
        );
      }
    }
  };

  const onBack = () =>
    dispatch({ type: actions.SET_PLACING_BID, payload: false });

  return (
    <AuctionBaseCard
      productName={"Yerba Mate"}
      variants={variantsAuction["variants"]}
      variantIndex={variantIndex}
      onVariantSelection={onVariantSelection}
    >
      {timeUntilStart > 0 ? (
        <>
          <AuctionBase stage="starting" targetDate={auctionStartTime + 1} />
        </>
      ) : timeUntilEnd.reduce((acc, v) => acc + v, 0) <= 0 ? (
        <>
          <AuctionBase stage="ended" targetDate={auctionStartTime + 1} />
        </>
      ) : (
        getAuctionKind(auction)
      )}
    </AuctionBaseCard>
  );
};

export default OmniAuction;
